import React, {useEffect, useRef, useState} from 'react';

/**
 * El título de un panel, editable con un clic (mockup: `h3.panel-title[contentEditable]`).
 * Enter confirma, Escape revierte, blur confirma normalizando espacios; vacío cae al placeholder.
 *
 * No existe nada parecido en el repo (el único `contentEditable` es el editor de fórmulas):
 * dos cuidados propios de React: no re-renderizar el nodo mientras tiene foco (pisa el cursor)
 * y frenar el `pointerdown` para que el drag del panel no arranque desde el título.
 */
export function InlineEditableTitle({value, placeholder, onCommit, readOnly = false, className = ''}) {
    const ref = useRef(null);
    const [editing, setEditing] = useState(false);
    const originalRef = useRef(value);

    useEffect(() => {
        if (!editing && ref.current && ref.current.textContent !== (value || placeholder || '')) {
            ref.current.textContent = value || placeholder || '';
        }
    }, [value, placeholder, editing]);

    const startEditing = () => {
        if (readOnly || editing) return;
        originalRef.current = value || '';
        setEditing(true);
        setTimeout(() => {
            if (!ref.current) return;
            if (!value) ref.current.textContent = '';
            ref.current.focus();
            const range = document.createRange();
            range.selectNodeContents(ref.current);
            const selection = window.getSelection();
            if (selection) { selection.removeAllRanges(); selection.addRange(range); }
        }, 0);
    };

    const commit = () => {
        if (!editing) return;
        const text = (ref.current ? ref.current.textContent : '').replace(/\s+/g, ' ').trim();
        setEditing(false);
        if (ref.current) ref.current.textContent = text || placeholder || '';
        if (text !== (value || '')) onCommit(text);
    };

    const onKeyDown = (e) => {
        if (!editing) return;
        if (e.key === 'Enter') {
            e.preventDefault();
            ref.current.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            if (ref.current) ref.current.textContent = originalRef.current || placeholder || '';
            setEditing(false);
        }
    };

    return (
        <h3
            ref={ref}
            className={`inf-panel-title ${className} ${editing ? '-editing' : ''} ${!value ? '-placeholder' : ''}`}
            contentEditable={editing}
            suppressContentEditableWarning
            spellCheck={false}
            role="textbox"
            aria-label={placeholder}
            data-editing={editing ? 'true' : 'false'}
            title={readOnly ? undefined : placeholder}
            onClick={startEditing}
            onKeyDown={onKeyDown}
            onBlur={commit}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {value || placeholder || ''}
        </h3>
    );
}
